import {
  ICompanyForm,
  ICompanyResponse,
  IPerson,
  IPersonForm,
} from 'modules/common/CreatePersonModal/types';
import {
  CustomerHttpService,
  PersonHttpService,
} from 'modules/customer/services';
import React, {
  createContext,
  ReactNode,
  useContext,
  useReducer,
  useState,
} from 'react';
import {
  EMaritalStatus,
  EPersonFunctions,
  ESex,
  IPersonAction,
  IPersonBusinessList,
  IPersonState,
  sexOptionLabel,
  TRegisterPersonData,
  TCreatePersonDataRequest,
  TPersonBusinessParams,
} from './person.interfaces';
import { personActionType } from './personActionType';
import { personInitialState, personReducer } from './personReducer';
import { TCreateBusinessDataRequest } from '../CustomerProvider';
import { IRelativePayload } from 'modules/customer/services/api/interfaces';
import { IUpatePersonData } from 'modules/customer/services/api/usePersonService';
import { IDocument } from 'app/components/UploadNewVersion/interfaces';
import { IUpdateBusinessData } from 'modules/customer/services/api/useCustomerService';
import {
  DocumentTypeObject,
  MaritialPropertyObject,
  MaritialStatusObject,
} from './person.constants';
import {
  EFunctions,
  PixType,
  TRegisterBusinessData,
} from '../CustomerProvider/customer.interfaces';
import { TProducts } from 'modules/products/context';

export enum EPersonType {
  PERSON = 'PERSON',
  COMPANY = 'COMPANY',
}

export interface IPersonProviderProps {
  children?: React.ReactNode;
}

interface PersonPropsContext {
  formType?: { label: string; value: EPersonType };
  setFormType: (value?: { label: string; value: EPersonType }) => void;
  state: IPersonState;
  dispatch: React.Dispatch<IPersonAction>;
  setPersonData: (data: Partial<TRegisterPersonData>) => void;
  setBusinessData: (data: Partial<TRegisterBusinessData>) => void;
  setProductData: (data: Partial<TProducts>) => void;
  setFunctionType: (data: {
    label: string;
    value: EPersonFunctions | EFunctions;
  }) => void;
  person: IPersonForm;
  setPerson: (person: IPersonForm) => void;
  personCompany: ICompanyForm;
  setPersonCompany: (company: ICompanyForm) => void;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  setCreatedBusinessId: (value: string) => void;
  setCreatedPersonId: (value: string) => void;
  createdPersonId?: string;
  createdBusinessId?: string;
  clearContext: () => void;
  // HK Renomear interface:
  createPerson: (person: TCreatePersonDataRequest) => Promise<IPerson>;
  updatePerson: (
    personId: string,
    personData: IUpatePersonData,
  ) => Promise<IPerson>;
  createRepresentative: (
    businessId: string,
    representativesList?: IRelativePayload[],
  ) => Promise<boolean>;
  createCompany: (
    company: TCreateBusinessDataRequest,
  ) => Promise<ICompanyResponse | undefined>;
  updateCompany: (
    businessId: string,
    payload: IUpdateBusinessData,
  ) => Promise<ICompanyResponse | undefined>;
  setRepresentatives: (representatives: IPerson[]) => void;
  representatives: IPerson[];
  getPersonBusinessList: (
    params: TPersonBusinessParams,
  ) => Promise<IPersonBusinessList | null>;
  setDocuments: (documents?: IDocument[]) => void;
  documents?: IDocument[];
  getPersonByTaxId: (taxId: string) => Promise<boolean>;
}

const PersonContext = createContext<PersonPropsContext>(
  {} as PersonPropsContext,
);

export const PersonProvider = ({ children }: { children?: ReactNode }) => {
  /* States */
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formType, setFormType] = useState<{
    label: string;
    value: EPersonType;
  }>();
  const [person, setPerson] = useState<IPersonForm>({} as IPersonForm);
  const [createdPersonId, setCreatedPersonId] = useState<string>();
  const [createdBusinessId, setCreatedBusinessId] = useState<string>();
  const [representatives, setRepresentatives] = useState<IPerson[]>([]);
  const [personCompany, setPersonCompany] = useState<ICompanyForm>(
    {} as ICompanyForm,
  );
  const [documents, setDocuments] = useState<IDocument[]>();
  // HK
  const [state, dispatch] = useReducer(personReducer, personInitialState);

  const clearContext = () => {
    setCreatedPersonId(undefined);
    setPerson({} as IPersonForm);
    setPersonCompany({} as ICompanyForm);
    setFormType(undefined);
  };

  const setPersonData = (data: Partial<TRegisterPersonData>) => {
    dispatch({
      type: personActionType.SET_REGISTER_PERSON_DATA,
      payload: data,
    });
  };

  const setProductData = (data: Partial<TProducts>) => {
    dispatch({
      type: personActionType.SET_PRODUCT_DATA,
      payload: data,
    });
  };

  const setBusinessData = (data: Partial<TRegisterBusinessData>) => {
    dispatch({
      type: personActionType.SET_REGISTER_BUSINESS_DATA,
      payload: data,
    });
  };

  const setFunctionType = (data: {
    label: string;
    value: EPersonFunctions | EFunctions;
  }) => {
    dispatch({
      type: personActionType.SET_FUNCTION_TYPE,
      payload: data,
    });
  };

  const createPerson = async (payload: TCreatePersonDataRequest) => {
    setIsLoading(true);

    try {
      const personService = new PersonHttpService();
      const res = await personService.creatPerson(payload);

      if (res.data.id) {
        setCreatedPersonId(res.data.id);
        return res.data;
      }

      return undefined;
    } catch (error) {
      console.error('Erro ao criar uma pessoa:', error);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePerson = async (
    personId: string,
    personData: IUpatePersonData,
  ) => {
    setIsLoading(true);
    try {
      const personService = new PersonHttpService();
      const res = await personService.updatePerson(personId, personData);
      if (res.data.id) {
        setCreatedPersonId(res.data.id);
        return res.data;
      }

      return undefined;
    } catch (error) {
      console.error('Erro ao editar uma pessoa:', error);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  const getPersonBusinessList = async (
    params: TPersonBusinessParams,
  ): Promise<IPersonBusinessList | null> => {
    setIsLoading(true);
    try {
      const service = new PersonHttpService();
      const personList = await service.getPersonBusinessList(
        params.page,
        params.searchParameterName,
        params.searchParameterValue,
        params.itemsPerPage,
      );
      return personList;
    } catch (error) {
      console.error('Ocorreu um erro ao buscar a lista de pessoas', error);
    } finally {
      setIsLoading(false);
    }
    return null;
  };

  const createRepresentative = async (
    businessId: string,
    representativesList?: IRelativePayload[],
  ) => {
    setIsLoading(true);
    try {
      const customerService = new CustomerHttpService();
      const createBusinessRelationPromises = representativesList?.map(
        async (representative) => {
          await customerService.createRelative(businessId, representative);
        },
      );
      await Promise.all(createBusinessRelationPromises || []);
      return true;
    } catch (error) {
      console.error('Erro ao adicionar um representante', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const createCompany = async (payload: TCreateBusinessDataRequest) => {
    setIsLoading(true);

    try {
      const customerService = new CustomerHttpService();
      const res = await customerService.creatBusiness(payload);

      if (res.data.id) {
        setCreatedBusinessId(res.data.id);
        return res.data;
      }

      return undefined;
    } catch (error) {
      console.error('Erro ao criar uma empresa', error);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCompany = async (
    personId: string,
    payload: IUpdateBusinessData,
  ) => {
    setIsLoading(true);
    try {
      const customerService = new CustomerHttpService();
      const res = await customerService.updateBusiness(personId, payload);
      if (res.data.id) {
        setCreatedPersonId(res.data.id);
        return res.data;
      }

      return undefined;
    } catch (error) {
      console.error('Erro ao editar uma empresa:', error);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  const getPersonByTaxId = async (taxId: string): Promise<boolean> => {
    try {
      setIsLoading(true);

      const personService = new PersonHttpService();
      const personResponse = await personService.getPersonList(
        0,
        'taxpayer_id',
        taxId,
        undefined,
        true,
      );

      if (personResponse && personResponse.content.length > 0) {
        const findPerson = personResponse.content[0];
        const parsedPerson: IPersonForm = {
          id: findPerson.id,
          full_name: findPerson.full_name,
          address: {
            city: findPerson.address?.city
              ? {
                  label: findPerson.address?.city,
                  value: findPerson.address?.city,
                }
              : undefined,
            country_code: findPerson.address?.country_code,
            district: findPerson.address?.district,
            postal_code: findPerson.address?.postal_code,
            state_code: findPerson.address?.state_code
              ? {
                  label: findPerson.address?.state_code,
                  value: findPerson.address?.state_code,
                }
              : undefined,
            street_name: findPerson.address?.street_name,
            street_number: findPerson?.address?.street_number || '0',
            extra_info: findPerson.address?.extra_info,
          },
          birth_date: findPerson.birth_date,
          birthplace: findPerson.birthplace,
          email_address: findPerson.email_address,
          external_bank_account: findPerson.external_bank_account,
          monthly_income: findPerson.monthly_income?.toString(),
          mothers_name: findPerson.mothers_name,
          nationality: findPerson.nationality,
          occupation: findPerson.occupation,
          spouse_date_of_birth: findPerson.spouse_date_of_birth,
          spouse_full_name: findPerson?.spouse_full_name,
          spouse_taxpayer_id: findPerson?.spouse_taxpayer_id ?? undefined,
          taxpayer_id: findPerson.taxpayer_id,
          phone: `${findPerson.phone.area_code}${findPerson.phone.number}`,
          pix: findPerson.pix
            ? {
                key: findPerson.pix.key,
                key_type: findPerson.pix.key_type as PixType,
              }
            : undefined,
          sex: findPerson.sex
            ? {
                label: sexOptionLabel[findPerson.sex as ESex],
                value: findPerson.sex as ESex,
              }
            : undefined,
          pep: findPerson?.pep
            ? { label: 'Sim', value: 'true' }
            : { label: 'Não', value: 'false' },
          identification: {
            document_issue_date: findPerson.id_document?.issue_date,
            document_issuer: findPerson.id_document?.issuer,
            document_number: findPerson.id_document?.number,
            document_type: findPerson.id_document?.type
              ? {
                  label: DocumentTypeObject[findPerson.id_document?.type],
                  value: findPerson.id_document?.type,
                }
              : undefined,
          },
          marital_property_system: findPerson.marital_property_system
            ? {
                label:
                  MaritialPropertyObject[findPerson.marital_property_system],
                value: findPerson.marital_property_system,
              }
            : undefined,
          marital_status: findPerson.marital_status
            ? {
                label:
                  MaritialStatusObject[
                    findPerson.marital_status as EMaritalStatus
                  ],
                value: findPerson.marital_status,
              }
            : undefined,
        };

        setPerson({ ...parsedPerson, taxpayer_id: taxId });
        return true;
      }

      setPerson({ ...person, taxpayer_id: taxId });
      return false;
    } catch (err) {
      console.error('Error fetching person data:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PersonContext.Provider
      value={{
        setCreatedBusinessId,
        documents,
        setDocuments,
        setCreatedPersonId,
        state,
        dispatch,
        setPersonData,
        setBusinessData,
        setProductData,
        setFunctionType,
        formType,
        setFormType,
        person,
        setPerson,
        createPerson,
        isLoading,
        createdPersonId,
        setIsLoading,
        personCompany,
        setPersonCompany,
        clearContext,
        createCompany,
        createRepresentative,
        representatives,
        setRepresentatives,
        getPersonBusinessList,
        createdBusinessId,
        updatePerson,
        updateCompany,
        getPersonByTaxId,
      }}
    >
      {children}
    </PersonContext.Provider>
  );
};

const usePersonContext = () => {
  const context = useContext(PersonContext);
  return context;
};

export { PersonProvider as default, usePersonContext };
